<template>
  <div>
    <VersionControlPopup
      ref="version-control-popup"
      :current-file="localFile"
      :id="id"
      :folder-type="folderType"
      @refresh="emitRefresh"
    />
    <ul class="list-group">
      <li
        class="list-group-item"
        :style="{
          borderLeft: 'none',
          borderRight: 'none',
          paddingLeft: `${20 * depth + 20}px`,
        }"
        :key="localFile.name"
      >
        <div class="file-name d-flex align-items-center">
          <font-awesome-icon
            :color="getIconColor(localFile.name)"
            :icon="getFileIcon(localFile.name)"
            @click="useCustoms ? customDownload(localFile) : download(localFile)"
          ></font-awesome-icon>
          <div
            class="d-flex flex-column justify-content-center"
            @click="useCustoms ? customDownload(localFile) : download(localFile)"
            style="width: 100%"
          >
            <div class="d-flex align-items-center ml-2" style="height: 40px">
              <span>{{
                `${localFile.name}${
                  fileUpload && fileUpload.status === "selected" ? " (old)" : ""
                }`
              }}</span>
              <span
                v-if="
                  fileUpload &&
                  fileUpload.status &&
                  fileUpload.status !== 'selected'
                "
                class="ml-2"
              >
                <el-tooltip
                  v-if="fileUpload.status === 'failed'"
                  effect="dark"
                  placement="top"
                  :content="fileUpload.high_msg || 'Upload failed'"
                >
                  <font-awesome-icon
                    size="lg"
                    :color="getAlertIconColor(fileUpload.status)"
                    :icon="getAlertIcon(fileUpload.status)"
                  />
                </el-tooltip>
                <font-awesome-icon
                  v-else
                  size="lg"
                  :color="getAlertIconColor(fileUpload.status)"
                  :icon="getAlertIcon(fileUpload.status)"
                />
              </span>
              <span
                v-if="fileUpload && fileUpload.status === 'selected'"
                class="ml-2"
                style="line-height: 40px"
              >
                ---> {{ `${fileUpload.fileName} (new)` }}</span
              >
            </div>
          </div>
          <div
            class="d-flex align-items-center justify-content-center"
            style="width: 200px"
          >
            <div
              v-if="localFile.errors && localFile.errors.length"
              class="show-details-button"
              @click="() => showErrorDetails = !showErrorDetails"
            >
              {{ showErrorDetails ?  'Hide details': 'Show details'}}
              <div class="w-100 mt-1 align-items-center justify-content-center text-center">
                <font-awesome-icon
                  class="warning-icon"
                  icon="exclamation-triangle"
                />
                <span v-if="localFile.errors.length">
                  ( {{ localFile.errors.length }} )
                </span>
              </div>
            </div>
          </div>
          <div
            class="d-flex align-items-center justify-content-center"
            style="width: 100px"
          >
<!--            v-if="localFile.file_type != 'request_form'"-->
            <el-upload
              class="upload-file"
              :ref="`upload-${localFile.name}`"
              :show-file-list="false"
              :headers="{ Authorization: authToken }"
              :data="{
                filename: localFile.name,
                filetype: localFile.file_type,
                foldertype: folderType
              }"
              :on-change="(f, flist) => handleChange(f)"
              :on-success="(res) => handleSuccess(res)"
              :action="`${baseUrl}/study/${id}/upload`"
              :auto-upload="false"
            >
              <el-button
                :disabled="uploadDisabled"
                :class="{
                  'upload-button': true,
                  visible: !fileUpload || fileUpload.status !== 'selected',
                }"
                slot="trigger"
                size="small"
                type="primary"
              >
                Update
              </el-button>
              <el-button
                :class="{
                  'upload-button': true,
                  visible: fileUpload && fileUpload.status === 'selected',
                }"
                size="small"
                type="success"
                @click.stop="useCustoms ? customUpload() : submitUpload(localFile.name)"
                >Confirm</el-button
              >
              <el-button
                :class="{
                  'upload-button': true,
                  visible: fileUpload && fileUpload.status === 'selected',
                }"
                class="mt-1 ml-0"
                size="small"
                type="warning"
                @click.stop="useCustoms ? customCancelUpload() : cancelUpload(localFile.name)"
                >Cancel</el-button
              >
              <el-button
                  v-if="showVersionControlButton"
                :disabled="uploadDisabled"
                class="mt-1 ml-0"
                tslot="trigger"
                size="small"
                type="primary"
                @click="
                  () => {
                    $refs['version-control-popup'].showPopup();
                  }
                "
              >
                Version Control
              </el-button>
            </el-upload>
          </div>
        </div>
        <ErrorDetailsTable
          v-if="localFile.errors && showErrorDetails"
          class="mt-3"
          :errors="localFile.errors"
        />
      </li>
    </ul>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import studyActions from '../../store/modules/studies/actionTypes'
import VersionControlPopup from '../FileStructure/VersionControlPopup'
import ErrorDetailsTable from '@/components/FileStructure/ErrorDetailsTable'

export default {
  name: 'FileList',
  emits: ['update:file', 'refresh', 'custom-download', 'custom-cancel-upload', 'custom-upload'],
  components: { VersionControlPopup, ErrorDetailsTable },
  props: {
    file: Object,
    depth: Number,
    folderType: String,
    showVersionControlButton: {
      type: Boolean,
      default: true
    },
    useCustoms: {
      type: Boolean,
      default: false
    }
  },
  data () {
    const configuredBaseURL = process.env.VUE_APP_IRIS_API_URL_DEV_PROD || '/api'
    const useSameOriginProxy = process.env.VUE_APP_USE_LOCAL_API_PROXY === 'true'
    const resolvedBaseURL = useSameOriginProxy ? '/api' : configuredBaseURL

    return {
      localFile: { ...this.file },
      baseUrl: resolvedBaseURL,
      id: this.$route.params.id,
      authToken: window.localStorage.getItem('auth_token_default'),
      uploadInterval: null,
      fileUpload: null,
      uploadDisabled: false,
      showErrorDetails: false
    }
  },
  methods: {
    ...mapActions({
      getFileUploadJobStatus: studyActions.CHECK_STUDY_FILE_UPLOAD_STATUS
    }),
    download (f) {
      this.axios({
        url: '/study/' + this.id + '/study-files-path/' + f.name,
        method: 'GET',
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', f.name)
        document.body.appendChild(link)
        link.click()
      })
    },
    getFileIcon (fileName) {
      const type = (fileName || '').split('.').pop()
      switch (type) {
        case 'doc':
          return 'file-word'
        case 'docx':
          return 'file-word'
        case 'ppt':
          return 'file-powerpoint'
        case 'pptx':
          return 'file-powerpoint'
        case 'pdf':
          return 'file-pdf'
        case 'xlsx':
          return 'file-excel'
        case 'xls':
          return 'file-excel'
        default:
          return 'file-alt'
      }
    },
    getAlertIcon (status) {
      switch (status) {
        case 'queued':
          return 'pause-circle'
        case 'started':
          return 'circle-notch'
        case 'failed':
          return 'times-circle'
        case 'finished':
          return 'check-circle'
        default:
          return ''
      }
    },
    getAlertIconColor (status) {
      switch (status) {
        case 'queued':
          return '#DA7B11'
        case 'started':
          return '#2D5897'
        case 'failed':
          return '#CE462E'
        case 'finished':
          return '#187240'
        default:
          return '#444444'
      }
    },
    getIconColor (fileName) {
      const type = (fileName || '').split('.').pop()
      switch (type) {
        case 'doc':
          return '#2D5897'
        case 'docx':
          return '#2D5897'
        case 'ppt':
          return '#CE462E'
        case 'pptx':
          return '#CE462E'
        case 'pdf':
          return '#FF0000'
        case 'xlsx':
          return '#187240'
        case 'xls':
          return '#187240'
        default:
          return '#444444'
      }
    },
    handleChange (file) {
      switch (this.localFile.status) {
        case 'ready':
          this.fileUpload = {
            fileName: this.localFile.name,
            status: 'selected'
          }
          if (this.useCustoms) {
            this.setOldFile(this.localFile)
            this.customOnChange(file)
          }
          break
        case 'fail':
          this.fileUpload = {
            fileName: this.localFile.name,
            status: 'failed'
          }
          if (this.useCustoms) {
            this.setOldFile(this.localFile)
            this.customOnChange(file)
          }
          break
        default:
          console.log(this.localFile.status)
      }
    },
    handleSuccess (res) {
      if (res === 'success') {
        this.uploadDisabled = false
        this.emitRefresh()
      } else if (!res || res === 'No such job') {
        this.fileUpload = {
          high_msg: 'Upload failed',
          status: 'failed'
        }
        this.uploadDisabled = false
      } else {
        try {
          this.uploadInterval = setInterval(async () => {
            const data = await this.getFileUploadJobStatus({
              jobId: res,
              studyId: this.id
            })
            if (data && data.status) {
              this.fileUpload = data
              if (['finished', 'failed'].includes(data.status)) {
                clearInterval(this.uploadInterval)
                this.uploadDisabled = false
                if (data.filename && data.filename !== this.localFile.name) {
                  this.localFile.name = data.filename
                }
              }
            }
          }, 2000)
        } catch {
          clearInterval(this.uploadInterval)
          this.uploadDisabled = false
        }
      }
    },
    submitUpload (id) {
      this.uploadDisabled = true
      const refName = `upload-${id}`
      this.$refs[refName].submit()
      this.fileUpload = null
    },
    cancelUpload (id) {
      const refName = `upload-${id}`
      this.$refs[refName].clearFiles()
      this.fileUpload = null
    },
    emitRefresh () {
      this.$emit('refresh')
    },
    async customDownload (f) {
      await this.$emit('custom-download', f)
    },
    customCancelUpload () {
      this.$emit('custom-cancel-upload')
      this.fileUpload = null
    },
    async customUpload () {
      this.uploadDisabled = true
      await this.$emit('custom-upload')
      this.fileUpload = null
    },
    customOnChange (f) {
      this.$emit('custom-on-change', f)
    },
    setOldFile (f) {
      this.$emit('set-old-file', f)
    }
  },
  watch: {
    // Compare by value: both branches assign a freshly spread object, so an
    // identity check can never be false and the pair never settles - each pass
    // allocates more objects and re-triggers the other watcher.
    file: {
      deep: true,
      handler (value) {
        if (!_.isEqual(value, this.localFile)) {
          this.localFile = _.cloneDeep(value)
        }
      }
    },
    localFile: {
      deep: true,
      handler (value) {
        if (!_.isEqual(value, this.file)) {
          this.$emit('update:file', _.cloneDeep(value))
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../assets/scss/variables';

.warning-icon {
  font-size: 15px;
  color: $red-600;
}
.list-group-item {
  padding-top: 5px;
  padding-bottom: 5px;
  transition: 0.15s linear;
}
.list-group-item:hover {
  background-color: rgba(200, 200, 200, 0.1);
}
.file-name {
  cursor: pointer;
}

.upload-button {
  display: none;
}

.upload-button.visible {
  display: unset;
}
.show-details-button {
  font-size: 12px;
  background-color: #409EFF;
  color: #fff;
  border-radius: .3rem;
  border: 1px solid #409EFF;
  padding: .5rem 1rem;
  &:hover {
    background-color: #66b1ff;
    border: 1px solid #66b1ff;
  }
}
</style>
